export interface OptionsLiquidityPolicy {
    symbol: 'NIFTY' | 'BANKNIFTY' | 'FINNIFTY';
    warningAvgSpread: number;
    warningAvgSpreadPct: number;
    warningAvgDepth: number;
    overlayMaxSpreadPct: number;
    overlaySpreadWeight: number;
    overlayDepthWeight: number;
    elevatedScore: number;
    criticalScore: number;
    clusterHysteresisUpWindows: number;
    clusterHysteresisDownWindows: number;
}

type PolicySymbol = OptionsLiquidityPolicy['symbol'];

function parseEnvNumber(key: string, fallback: number): number {
    const value = (import.meta.env as Record<string, unknown>)[key];
    if (typeof value !== 'string' || value.trim() === '') return fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

function clampInteger(value: number, min: number, max: number): number {
    return Math.round(clamp(value, min, max));
}

function applyEnvOverrides(base: OptionsLiquidityPolicy): OptionsLiquidityPolicy {
    const prefix = `VITE_OPTIONS_LIQ_${base.symbol}_`;

    const warningAvgSpread = clamp(parseEnvNumber(`${prefix}WARNING_SPREAD`, base.warningAvgSpread), 0.1, 1000);
    const warningAvgSpreadPct = clamp(parseEnvNumber(`${prefix}WARNING_SPREAD_PCT`, base.warningAvgSpreadPct), 0.1, 100);
    const warningAvgDepth = clamp(parseEnvNumber(`${prefix}WARNING_DEPTH`, base.warningAvgDepth), 1, 1000000);
    const overlayMaxSpreadPct = clamp(parseEnvNumber(`${prefix}OVERLAY_MAX_SPREAD_PCT`, base.overlayMaxSpreadPct), 0.1, 100);

    const spreadWeightRaw = clamp(parseEnvNumber(`${prefix}OVERLAY_SPREAD_WEIGHT`, base.overlaySpreadWeight), 0, 1);
    const depthWeightRaw = clamp(parseEnvNumber(`${prefix}OVERLAY_DEPTH_WEIGHT`, base.overlayDepthWeight), 0, 1);
    const totalWeight = spreadWeightRaw + depthWeightRaw;
    const overlaySpreadWeight = totalWeight > 0 ? spreadWeightRaw / totalWeight : base.overlaySpreadWeight;
    const overlayDepthWeight = totalWeight > 0 ? depthWeightRaw / totalWeight : base.overlayDepthWeight;

    const elevatedScore = clamp(parseEnvNumber(`${prefix}ELEVATED_SCORE`, base.elevatedScore), 0.01, 0.99);
    const criticalBase = clamp(parseEnvNumber(`${prefix}CRITICAL_SCORE`, base.criticalScore), 0.01, 1);
    const criticalScore = Math.max(elevatedScore, criticalBase);
    const hysteresisLegacy = clampInteger(parseEnvNumber(`${prefix}HYSTERESIS_WINDOWS`, base.clusterHysteresisUpWindows), 1, 10);
    const clusterHysteresisUpWindows = clampInteger(parseEnvNumber(`${prefix}HYSTERESIS_UP`, hysteresisLegacy), 1, 10);
    const clusterHysteresisDownWindows = clampInteger(parseEnvNumber(`${prefix}HYSTERESIS_DOWN`, hysteresisLegacy), 1, 10);

    return {
        ...base,
        warningAvgSpread,
        warningAvgSpreadPct,
        warningAvgDepth,
        overlayMaxSpreadPct,
        overlaySpreadWeight,
        overlayDepthWeight,
        elevatedScore,
        criticalScore,
        clusterHysteresisUpWindows,
        clusterHysteresisDownWindows,
    };
}

const DEFAULT_POLICY: OptionsLiquidityPolicy = {
    symbol: 'NIFTY',
    warningAvgSpread: 1.5,
    warningAvgSpreadPct: 4,
    warningAvgDepth: 150,
    overlayMaxSpreadPct: 8,
    overlaySpreadWeight: 0.65,
    overlayDepthWeight: 0.35,
    elevatedScore: 0.45,
    criticalScore: 0.7,
    clusterHysteresisUpWindows: 2,
    clusterHysteresisDownWindows: 2,
};

const POLICY_MAP: Record<PolicySymbol, OptionsLiquidityPolicy> = {
    NIFTY: {
        symbol: 'NIFTY',
        warningAvgSpread: 1.2,
        warningAvgSpreadPct: 3.5,
        warningAvgDepth: 180,
        overlayMaxSpreadPct: 7,
        overlaySpreadWeight: 0.62,
        overlayDepthWeight: 0.38,
        elevatedScore: 0.42,
        criticalScore: 0.68,
        clusterHysteresisUpWindows: 2,
        clusterHysteresisDownWindows: 2,
    },
    BANKNIFTY: {
        symbol: 'BANKNIFTY',
        warningAvgSpread: 2,
        warningAvgSpreadPct: 4.8,
        warningAvgDepth: 220,
        overlayMaxSpreadPct: 9,
        overlaySpreadWeight: 0.68,
        overlayDepthWeight: 0.32,
        elevatedScore: 0.46,
        criticalScore: 0.72,
        clusterHysteresisUpWindows: 2,
        clusterHysteresisDownWindows: 2,
    },
    FINNIFTY: {
        symbol: 'FINNIFTY',
        warningAvgSpread: 1.6,
        warningAvgSpreadPct: 4.2,
        warningAvgDepth: 130,
        overlayMaxSpreadPct: 8,
        overlaySpreadWeight: 0.64,
        overlayDepthWeight: 0.36,
        elevatedScore: 0.44,
        criticalScore: 0.7,
        clusterHysteresisUpWindows: 2,
        clusterHysteresisDownWindows: 2,
    },
};

export function getOptionsLiquidityPolicy(symbol: string): OptionsLiquidityPolicy {
    const normalized = symbol.toUpperCase() as PolicySymbol;
    const base = POLICY_MAP[normalized] || DEFAULT_POLICY;
    return applyEnvOverrides(base);
}
